import React, { useState, useEffect, useRef } from "react";
import "./documentation.scss";
import Dashboard from "../../layout/Dashboard/Dashboard";
import CookieManager from "../../components/Cookies/CookieManager";
import { useTranslation } from "react-i18next";
import "../../i18n";
import DocSidebar from "./components/DocSidebar";
import GettingStarted from "./sections/GettingStarted";
import DataCharts from "./sections/DataCharts";
import Analytics from "./sections/Analytics";

const Documentation = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("getting-started");
  const contentRef = useRef(null);

  // Section definitions for sidebar navigation
  const sections = [
    {
      id: "getting-started",
      title: t("docs.gettingStarted.title"),
      subsections: [
        { id: "welcome", title: t("docs.gettingStarted.welcome") },
        { id: "navigation", title: t("docs.gettingStarted.navigation") },
        { id: "quick-start", title: t("docs.gettingStarted.quickStart") },
      ],
    },
    {
      id: "data-charts",
      title: t("docs.dataCharts.title"),
      subsections: [
        { id: "datasets", title: t("docs.dataCharts.datasets") },
        { id: "creating-charts", title: t("docs.dataCharts.creatingCharts") },
        { id: "pivot-tables", title: t("docs.dataCharts.pivotTables") },
        { id: "chart-types", title: t("docs.dataCharts.chartTypes") },
        { id: "geographic-maps", title: t("docs.dataCharts.geographicMaps") },
      ],
    },
    {
      id: "analytics",
      title: t("docs.analytics.title"),
      subsections: [
        { id: "recommendations", title: t("docs.analytics.recommendations") },
        { id: "risk-scores", title: t("docs.analytics.riskScores") },
        { id: "correlation", title: t("docs.analytics.correlation") },
        { id: "fusion", title: t("docs.analytics.fusion") },
      ],
    },
  ];

  // Scroll spy using Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -70% 0px",
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all section elements
    const sectionElements = document.querySelectorAll(".doc-section");
    sectionElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div>
      <CookieManager />
      <Dashboard>
        <div className="documentation-container">
          <DocSidebar
            sections={sections}
            activeSection={activeSection}
            onSectionClick={scrollToSection}
          />
          <div className="documentation-content" ref={contentRef}>
            <h1 className="doc-main-title">{t("docs.mainTitle")}</h1>
            <p className="doc-intro">{t("docs.intro")}</p>

            <GettingStarted />
            <DataCharts />
            <Analytics />

            {/* Back to top button */}
            <button
              className="back-to-top"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Back to top"
            >
              <i className="bi bi-arrow-up"></i>
            </button>
          </div>
        </div>
      </Dashboard>
    </div>
  );
};

export default Documentation;
